import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import {
  TOPIC_LABEL_KEY,
  useAssistantConversationsQuery,
} from "@/partner/entities/assistant";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { BuyerEmailCell } from "@/partner/shared/components/BuyerEmailCell";
import type { AssistantConversationsListQueryType } from "@/types/graphql/partner.generated";
import { buildConversationsFilter } from "../model/buildConversationsFilter";

type ConversationRow =
  AssistantConversationsListQueryType["assistantConversations"]["nodes"][number];

interface ConversationsListProps {
  onOpenConversation: (id: string) => void;
}

export const ConversationsList: FC<ConversationsListProps> = ({
  onOpenConversation,
}) => {
  const { t, i18n } = useTranslation();
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error } = useAssistantConversationsQuery({
    variables: { page, rowsPerPage, filter: buildConversationsFilter(filter) },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.assistantConversations?.nodes ?? [];
  const count = data?.assistantConversations?.count ?? 0;

  const emptyValue = t("common.emptyDash");

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("conversations.empty")} />;
  }

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ maxWidth: 220 }}>
              {t("conversations.viewerColumn")}
            </TableCell>
            <TableCell sx={{ minWidth: 200 }}>
              {t("conversations.titleColumn")}
            </TableCell>
            <TableCell>{t("conversations.topicColumn")}</TableCell>
            <TableCell align="right" sx={{ width: 160 }}>
              {t("conversations.lastMessageColumn")}
            </TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              {t("conversations.messagesColumn")}
            </TableCell>
            <TableCell sx={{ width: 120 }}>
              {t("conversations.requestColumn")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row: ConversationRow) => (
            <TableRow
              key={row.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => onOpenConversation(row.id)}
            >
              <TableCell sx={{ maxWidth: 220 }}>
                <BuyerEmailCell
                  email={row.user?.email}
                  userId={row.user?.id}
                  emptyValue={emptyValue}
                />
              </TableCell>

              <TableCell sx={{ minWidth: 200 }}>
                <Typography variant="body2">
                  {row.title || t("conversations.noTitle")}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {row.topic ? t(TOPIC_LABEL_KEY[row.topic]) : emptyValue}
                </Typography>
              </TableCell>

              <TableCell align="right" sx={{ width: 160 }}>
                <Typography variant="body2" noWrap>
                  {interpolationFormat(
                    row.lastMessageAt,
                    "short",
                    i18n.language
                  )}
                </Typography>
              </TableCell>

              <TableCell align="right" sx={{ width: 120 }}>
                <Typography variant="body2">{row.messageCount}</Typography>
              </TableCell>

              <TableCell sx={{ width: 120 }}>
                {row.hasRequest ? (
                  <Chip size="small" label={t("conversations.hasRequest")} />
                ) : (
                  <Typography variant="body2">{emptyValue}</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
